import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  createDonationImpactUpdate,
  listDonationImpactUpdates,
} from '../../services/donationImpactService';

const { FiActivity, FiPlus, FiX, FiSend } = FiIcons;

function formatImpactDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Date pending';
}

export default function DonationImpactUpdates({ campaign, memorial, canManage }) {
  const campaignId = campaign?.id;
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!campaignId) {
        setUpdates([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await listDonationImpactUpdates(campaignId);
      if (!cancelled) {
        setUpdates(result?.status === 'success' ? result.data ?? [] : []);
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [campaignId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setSaving(true);

    const result = await createDonationImpactUpdate({
      campaign_id: Number(campaignId),
      memorial_id: memorial?.id ? String(memorial.id) : null,
      update_title: title.trim(),
      update_message: message.trim(),
      impact_amount: amount ? Number(amount) : null,
      impact_date: new Date(`${date}T12:00:00`).toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (result?.status === 'success') {
      setTitle('');
      setMessage('');
      setAmount('');
      setNotice('Update published.');
      const refreshed = await listDonationImpactUpdates(campaignId);
      if (refreshed?.status === 'success') setUpdates(refreshed.data ?? []);
    } else {
      setError(result?.error || 'Unable to publish this update.');
    }
    setSaving(false);
  };

  if (!campaignId) return null;

  return (
    <section className="rounded-3xl border border-brand-dark bg-white p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-gold/10 text-accent-gold">
            <SafeIcon icon={FiActivity} className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-serif text-lg text-accent-navy">Impact Updates</h4>
            <p className="text-xs text-text-muted">News from this campaign</p>
          </div>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => { setEditing(value => !value); setError(''); }}
            className="flex shrink-0 items-center gap-1.5 rounded-xl border border-brand-dark px-3 py-2 text-[9px] font-black uppercase tracking-widest text-accent-navy transition hover:border-accent-gold"
          >
            <SafeIcon icon={editing ? FiX : FiPlus} className="h-3.5 w-3.5" />
            {editing ? 'Close' : 'Post update'}
          </button>
        )}
      </div>

      {editing && canManage && (
        <form onSubmit={handleSubmit} className="mt-5 space-y-3 rounded-2xl bg-brand-light/50 p-4">
          <input
            required
            maxLength={120}
            value={title}
            onChange={event => setTitle(event.target.value)}
            placeholder="Update title"
            className="w-full rounded-xl border border-brand-dark bg-white px-3 py-2.5 text-sm outline-none focus:border-accent-gold"
          />
          <textarea
            required
            maxLength={4000}
            rows={4}
            value={message}
            onChange={event => setMessage(event.target.value)}
            placeholder="Share what the donations are making possible…"
            className="w-full resize-y rounded-xl border border-brand-dark bg-white px-3 py-2.5 text-sm outline-none focus:border-accent-gold"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs font-bold text-text-muted">
              Impact date
              <input
                required
                type="date"
                value={date}
                onChange={event => setDate(event.target.value)}
                className="mt-1 w-full rounded-xl border border-brand-dark bg-white px-3 py-2 text-sm font-normal"
              />
            </label>
            <label className="text-xs font-bold text-text-muted">
              Amount used (optional)
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={event => setAmount(event.target.value)}
                placeholder="0.00"
                className="mt-1 w-full rounded-xl border border-brand-dark bg-white px-3 py-2 text-sm font-normal"
              />
            </label>
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          {notice && <p role="status" className="text-sm text-green-700">{notice}</p>}
          <button
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-navy px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white transition hover:bg-accent-navy/90 disabled:opacity-60"
          >
            <SafeIcon icon={FiSend} className="h-4 w-4" />
            {saving ? 'Publishing…' : 'Publish update'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="mt-5 h-16 animate-pulse rounded-2xl bg-brand-light" />
      ) : updates.length === 0 ? (
        <p className="mt-5 rounded-2xl bg-brand-light/50 p-4 text-sm text-text-muted">
          No impact updates have been shared yet.
        </p>
      ) : (
        <ol className="mt-5 space-y-4">
          {updates.map((update, index) => (
            <li
              key={`${update?.campaign_id ?? campaignId}-${update?.impact_date ?? 'update'}-${index}`}
              className="border-l-2 border-accent-gold pl-4"
            >
              <time className="text-[10px] font-black uppercase tracking-widest text-accent-gold">
                {formatImpactDate(update?.impact_date ?? '')}
              </time>
              <h5 className="mt-1 text-sm font-bold text-accent-navy">
                {update?.update_title ?? ''}
              </h5>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-text-muted">
                {update?.update_message ?? ''}
              </p>
              {update?.impact_amount != null && (
                <p className="mt-2 text-xs font-bold text-accent-navy">
                  Reported impact: ${Number(update.impact_amount ?? 0).toLocaleString()}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}